import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  TextField,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  LinearProgress,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Campaign as PromoterIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  AttachMoney as RevenueIcon,
  CheckCircle as ActiveIcon,
  PendingActions as PendingIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Visibility as ViewIcon,
  Assignment as TaskIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

const PromoterManagement = () => {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState(0);
  const [promoterDialogOpen, setPromoterDialogOpen] = useState(false);
  const [selectedPromoter, setSelectedPromoter] = useState(null);

  // Mock promoters data
  const promoters = [
    {
      id: 1,
      name: "Rajesh Kumar",
      email: "rajesh@promoter.com",
      phone: "+91 98765 43210",
      location: "Mumbai, Maharashtra",
      status: "Active",
      joinDate: "2024-01-01",
      totalLeads: 45,
      convertedLeads: 12,
      conversionRate: 26.7,
      totalCommission: 60000,
      pendingCommission: 15000,
      rating: 4.8,
      tier: "Gold",
      activeTasks: 5,
      completedTasks: 28,
    },
    {
      id: 2,
      name: "Priya Sharma",
      email: "priya@promoter.com",
      phone: "+91 98765 43211",
      location: "Delhi, NCR",
      status: "Active",
      joinDate: "2024-01-05",
      totalLeads: 32,
      convertedLeads: 8,
      conversionRate: 25.0,
      totalCommission: 40000,
      pendingCommission: 10000,
      rating: 4.6,
      tier: "Silver",
      activeTasks: 3,
      completedTasks: 20,
    },
    {
      id: 3,
      name: "Amit Patel",
      email: "amit@promoter.com",
      phone: "+91 98765 43212",
      location: "Bangalore, Karnataka",
      status: "Active",
      joinDate: "2024-01-10",
      totalLeads: 18,
      convertedLeads: 4,
      conversionRate: 22.2,
      totalCommission: 20000,
      pendingCommission: 5000,
      rating: 4.3,
      tier: "Bronze",
      activeTasks: 2,
      completedTasks: 12,
    },
  ];

  const promoterTasks = [
    {
      id: 1,
      promoterName: "Rajesh Kumar",
      taskType: "School Visit",
      schoolName: "Greenwood High School",
      location: "Mumbai",
      assignedDate: "2024-01-15",
      dueDate: "2024-01-20",
      status: "In Progress",
      priority: "High",
      description: "Visit school to demonstrate product features",
    },
    {
      id: 2,
      promoterName: "Priya Sharma",
      taskType: "Follow-up Call",
      schoolName: "Sunshine Academy",
      location: "Delhi",
      assignedDate: "2024-01-14",
      dueDate: "2024-01-16",
      status: "Completed",
      priority: "Medium",
      description: "Follow up on demo request",
    },
    {
      id: 3,
      promoterName: "Amit Patel",
      taskType: "Lead Generation",
      schoolName: "New Lead - Excel Academy",
      location: "Bangalore",
      assignedDate: "2024-01-15",
      dueDate: "2024-01-18",
      status: "Pending",
      priority: "High",
      description: "Generate new lead from education expo",
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Inactive":
        return "error";
      case "In Progress":
        return "info";
      case "Completed":
        return "success";
      case "Pending":
        return "warning";
      default:
        return "default";
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case "Gold":
        return "#FFD700";
      case "Silver":
        return "#C0C0C0";
      case "Bronze":
        return "#CD7F32";
      default:
        return theme.palette.primary.main;
    }
  };

  const totalPromoters = promoters.length;
  const totalLeads = promoters.reduce((sum, p) => sum + p.totalLeads, 0);
  const totalConverted = promoters.reduce((sum, p) => sum + p.convertedLeads, 0);
  const totalCommission = promoters.reduce((sum, p) => sum + p.totalCommission, 0);
  const avgConversionRate = Math.round(
    promoters.reduce((sum, p) => sum + p.conversionRate, 0) / promoters.length
  );

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
              <PromoterIcon sx={{ color: theme.palette.primary.main }} />
              Promoter Management
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Manage promoters, assign tasks, and track performance
            </Typography>
          </Box>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setPromoterDialogOpen(true)}>
            Add Promoter
          </Button>
        </Box>

        {/* Key Metrics */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Total Promoters
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {totalPromoters}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  {promoters.filter((p) => p.status === "Active").length} Active
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Total Leads
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  {totalLeads}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  {totalConverted} Converted ({avgConversionRate}%)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Total Commission
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                  ₹{(totalCommission / 1000).toFixed(0)}K
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  {promoters.reduce((sum, p) => sum + p.pendingCommission, 0).toLocaleString()} Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.5, display: "block" }}>
                  Active Tasks
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                  {promoterTasks.filter((t) => t.status === "In Progress" || t.status === "Pending").length}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                  {promoterTasks.filter((t) => t.status === "Completed").length} Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card sx={{ mb: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Promoters" />
            <Tab label="Tasks" />
            <Tab label="Performance" />
          </Tabs>
        </Card>

        {/* Promoters Tab */}
        {selectedTab === 0 && (
          <Grid container spacing={2}>
            {promoters.map((promoter) => (
              <Grid item xs={12} md={4} key={promoter.id}>
                <Card
                  sx={{
                    border: `2px solid ${getTierColor(promoter.tier)}`,
                    "&:hover": {
                      boxShadow: 4,
                      transform: "translateY(-4px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
                          {promoter.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                            {promoter.name}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {[...Array(5)].map((_, i) => (
                              <StarIcon
                                key={i}
                                fontSize="small"
                                sx={{
                                  color: i < Math.floor(promoter.rating) ? theme.palette.warning.main : "text.disabled",
                                  fontSize: "0.875rem",
                                }}
                              />
                            ))}
                            <Typography variant="caption" sx={{ ml: 0.5, fontSize: "0.7rem" }}>
                              {promoter.rating}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Chip
                        label={promoter.tier}
                        size="small"
                        sx={{
                          bgcolor: getTierColor(promoter.tier),
                          color: "white",
                          fontSize: "0.7rem",
                          height: "22px",
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                        <LocationIcon fontSize="small" sx={{ fontSize: "0.7rem" }} />
                        {promoter.location}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}>
                        <PhoneIcon fontSize="small" sx={{ fontSize: "0.7rem" }} />
                        {promoter.phone}
                      </Typography>
                    </Box>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                            Leads
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.primary.main }}>
                            {promoter.totalLeads}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: "center", p: 1, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", display: "block" }}>
                            Converted
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.25rem", color: theme.palette.success.main }}>
                            {promoter.convertedLeads}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                          Conversion Rate
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 600 }}>
                          {promoter.conversionRate}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={promoter.conversionRate}
                        sx={{ height: 6, borderRadius: 3 }}
                        color={promoter.conversionRate >= 25 ? "success" : promoter.conversionRate >= 20 ? "info" : "warning"}
                      />
                    </Box>
                    <Box sx={{ mb: 2, p: 1, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                          Total Commission
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem", color: theme.palette.success.main }}>
                          ₹{promoter.totalCommission.toLocaleString()}
                        </Typography>
                      </Box>
                      {promoter.pendingCommission > 0 && (
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                            Pending
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem", color: theme.palette.warning.main }}>
                            ₹{promoter.pendingCommission.toLocaleString()}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button variant="outlined" size="small" fullWidth startIcon={<ViewIcon />}>
                        View Details
                      </Button>
                      <Button variant="outlined" size="small" fullWidth startIcon={<TaskIcon />}>
                        Assign Task
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Tasks Tab */}
        {selectedTab === 1 && (
          <Card>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search tasks..."
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
                  }}
                  sx={{ flexGrow: 1, minWidth: 250 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" defaultValue="all">
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Completed">Completed</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="contained" size="small" startIcon={<AddIcon />}>
                  Create Task
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Promoter</TableCell>
                      <TableCell>Task Type</TableCell>
                      <TableCell>School/Lead</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {promoterTasks.map((task) => (
                      <TableRow key={task.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                            {task.promoterName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={task.taskType}
                            size="small"
                            sx={{
                              fontSize: "0.7rem",
                              height: "22px",
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                            {task.schoolName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                            {task.location}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                            {task.dueDate}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={task.priority}
                            size="small"
                            color={task.priority === "High" ? "error" : task.priority === "Medium" ? "warning" : "info"}
                            sx={{ fontSize: "0.7rem", height: "22px" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={task.status}
                            size="small"
                            color={getStatusColor(task.status)}
                            sx={{ fontSize: "0.7rem", height: "22px" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="primary">
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Performance Tab */}
        {selectedTab === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                    Conversion Performance
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {promoters.map((promoter) => (
                      <Box key={promoter.id}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                            {promoter.name}
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                            {promoter.conversionRate}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={promoter.conversionRate}
                          sx={{ height: 6, borderRadius: 3 }}
                          color={promoter.conversionRate >= 25 ? "success" : promoter.conversionRate >= 20 ? "info" : "warning"}
                        />
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, fontSize: "1rem" }}>
                    Commission Summary
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {promoters.map((promoter) => (
                      <Box
                        key={promoter.id}
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem", mb: 0.5 }}>
                          {promoter.name}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                            Total: ₹{promoter.totalCommission.toLocaleString()}
                          </Typography>
                          {promoter.pendingCommission > 0 && (
                            <Typography variant="caption" sx={{ fontSize: "0.7rem", color: theme.palette.warning.main }}>
                              Pending: ₹{promoter.pendingCommission.toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Add Promoter Dialog */}
        <Dialog open={promoterDialogOpen} onClose={() => setPromoterDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Promoter</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Name" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone" size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Email" type="email" size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Location" size="small" />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Initial Tier</InputLabel>
                  <Select label="Initial Tier" defaultValue="Bronze">
                    <MenuItem value="Bronze">Bronze</MenuItem>
                    <MenuItem value="Silver">Silver</MenuItem>
                    <MenuItem value="Gold">Gold</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPromoterDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => { setPromoterDialogOpen(false); toast.success("Promoter added successfully!"); }}>
              Add Promoter
            </Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default PromoterManagement;

